import React, { useMemo, useState } from "react";
import { Plus, X, Search } from "lucide-react";
import {
  WIDGET_CATALOG,
  WIDGET_CATEGORIES,
} from "@/lib/dashboardWidgetCatalog";

/**
 * AddWidgetPicker — modal for the Edit-Mode "Add Widget" action.
 *
 * Filters the shipped catalog by:
 *   1. Widgets already present in the layout (excluded)
 *   2. User's effective permission set (excluded if the required
 *      permission is not granted). Future-Shop widgets are surfaced with
 *      an "Planned" tag if the user does have shop.* perms; otherwise
 *      they are hidden entirely from the picker (there's no value in
 *      letting a user pin a widget that will always render "Restricted").
 *
 * Backend permission-filtered aggregator remains the ultimate authority
 * — if a permission is revoked after add, the widget renders "Restricted"
 * even while still in the saved layout.
 */
export default function AddWidgetPicker({
  open,
  onClose,
  onPick,
  currentKeys = [],
  allowedPermissions = [],
}) {
  const [q, setQ] = useState("");
  const inLayout = useMemo(() => new Set(currentKeys), [currentKeys]);
  const perms = useMemo(() => new Set(allowedPermissions), [allowedPermissions]);

  const available = useMemo(() => {
    const term = q.trim().toLowerCase();
    return WIDGET_CATALOG.filter((w) => {
      if (inLayout.has(w.key)) return false;
      if (w.required_permission && !perms.has(w.required_permission)) return false;
      if (!term) return true;
      return (
        w.label.toLowerCase().includes(term) ||
        w.key.toLowerCase().includes(term) ||
        (w.category || "").toLowerCase().includes(term)
      );
    });
  }, [q, inLayout, perms]);

  const byCat = useMemo(() => {
    const m = new Map();
    for (const w of available) {
      if (!m.has(w.category)) m.set(w.category, []);
      m.get(w.category).push(w);
    }
    return WIDGET_CATEGORIES.filter((c) => m.has(c)).map((c) => ({
      category: c,
      items: m.get(c),
    }));
  }, [available]);

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="add-widget-picker"
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white dark:bg-graphite border border-coach-silver dark:border-graphite rounded-md shadow-lg flex flex-col max-h-[85vh]"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-coach-silver dark:border-graphite">
          <div>
            <div className="text-[10.5px] uppercase tracking-widest text-steel">Add Widget</div>
            <div className="font-display text-lg text-carbon dark:text-road-white">
              Widget Catalog
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="add-widget-close"
            className="text-steel hover:text-carbon dark:hover:text-road-white p-1"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-3 border-b border-coach-silver dark:border-graphite">
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-steel" />
            <input
              autoFocus
              data-testid="add-widget-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by label, category, or key…"
              className="w-full pl-7 pr-3 py-1.5 text-[13px] border border-coach-silver rounded-sm bg-white dark:bg-carbon focus:outline-none focus:border-coach-blue"
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-3">
          {byCat.length === 0 ? (
            <div className="text-center text-[12.5px] text-steel py-8" data-testid="add-widget-empty">
              No widgets match. All available widgets may already be on
              your dashboard, or none match your permission set.
            </div>
          ) : (
            byCat.map((g) => (
              <div key={g.category} className="mb-4">
                <div className="text-[10.5px] uppercase tracking-widest text-steel font-semibold mb-1.5">
                  {g.category}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {g.items.map((w) => (
                    <button
                      key={w.key}
                      type="button"
                      data-testid={`add-widget-${w.key}`}
                      onClick={() => { onPick(w.key); onClose(); }}
                      className="flex items-center gap-2 text-left px-2 py-1.5 rounded-sm border border-coach-silver hover:border-coach-blue hover:bg-road-white dark:hover:bg-carbon/40 transition"
                    >
                      <Plus size={13} className="text-coach-blue shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] text-carbon dark:text-road-white truncate">
                          {w.label}
                        </div>
                        <div className="text-[10.5px] text-steel truncate">
                          {w.key}
                          {w.is_future_placeholder && (
                            <span className="ml-1 text-touring-lime">· Planned</span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
